<template>
    <div class="settings-wrapper">
        <div
            v-if="!isMobileView"
            class="settings-tab-nav"
        >
            <tabs :value="settingsTabInView" @update:value="changeSettingsTab">
                <tab-list>
                    <tab value="general">General</tab>
                    <tab value="timePeriod">Time/Period</tab>
                    <tab value="boa">BOA</tab>
                    <tab value="chartsAndTables">Charts and Tables</tab>
                    <tab value="plants">Plants</tab>
                    <tab value="pools" :disabled="!hasPoolAccess">Pools</tab>
                    <tab value="heartbeat">Heartbeat</tab>
                </tab-list>
            </tabs>
        </div>
        <div class="settings-content">
            <div v-show="settingsTabInView === 'general'" class="settings-grid">
                <label for="lockAccount">Lock account</label>
                <div class="row">
                    <select-button
                        :model-value="selectedLockAccountSetting"
                        id="lockAccount"
                        option-label="name"
                        option-value="id"
                        :allow-empty="false"
                        :options="lockAccountOptions"
                        @update:model-value="updateSiteSetting('lockedAccount', $event)"
                    >
                        <template #option="{ option }">
                            <span class="select-button-option">
                                <span class="select-button-option-label">{{ option.name }}</span>
                                <info-icon-with-tooltip v-if="option.info" direction="top" :tooltip-text="option.info" />
                            </span>
                        </template>
                    </select-button>
                </div>
                <label for="tabUpdates">
                    Tab updates
                    <info-icon-with-tooltip direction="top" tooltipText="Please note that updating this will require other tabs to be manually refreshed." />
                </label>
                <div class="row">
                    <select-button
                        :model-value="selectedTabUpdatesSetting"
                        id="tabUpdates"
                        :options="tabUpdatesOptions"
                        option-label="name"
                        option-value="id"
                        :allow-empty="false"
                        @update:model-value="updateSiteSetting('tabUpdateValue', $event)"
                    >
                        <template #option="{ option }">
                            <span class="select-button-option">
                                <span class="select-button-option-label">{{ option.name }}</span>
                                <info-icon-with-tooltip direction="left" :tooltip-text="option.info" />
                            </span>
                        </template>
                    </select-button>
                </div>
                <label for="sideNavigation">Side navigation</label>
                <select-button
                    :model-value="selectedSideNavigationSetting"
                    id="sideNavigation"
                    :options="sideNavigationOptions"
                    option-label="name"
                    option-value="id"
                    :allow-empty="false"
                    @update:model-value="updateSiteSetting('sideNavVisible', $event === 'shown')"
                />
                <label for="defaultCountry">
                    Default country
                    <info-icon-with-tooltip direction="top" tooltipText="This will make the selected country the default for the site, for example when creating charts or dashboards. Selecting 'None' will use the most recently selected country." />
                </label>
                <country-selector
                    input-id="defaultCountry"
                    specific-country-access
                    is-site-wide-country-default
                    do-not-emit-on-create
                    @update:model-value="updateSiteSetting('country', $event.id)"
                />
                <label for="elexonDelayMinutes">Elexon report delay time</label>
                <div class="row">
                    <input-number
                        :model-value="selectedElexonDelayAlert"
                        input-id="elexonDelayMinutes"
                        show-buttons
                        :min="4"
                        @update:model-value="updateSiteSetting('minutesDelayToReport', $event)"
                    />
                    minutes
                </div>
            </div>
            <time-period-settings v-show="settingsTabInView === 'timePeriod'" />
            <div v-show="settingsTabInView === 'boa'">
                <div class="sub-section boa-sub-section">
                    <h4>Colours:</h4>
                    <div class="sub-row">
                        <div class="colour-selector-wrapper">
                            <label for="bidColour">Bid colour</label>
                            <colour-selector v-model:colour="bidColour" input-id="bidColour" @update:colour="updateBidColour" />

                            <label for="offerColour">Offer colour</label>
                            <colour-selector v-model:colour="offerColour" input-id="offerColour" @update:colour="updateOfferColour" />
                        </div>
                    </div>
                    <h4>Other settings:</h4>
                    <div class="sub-row">
                        <label for="orderAcceptedFirst">Order accepted first</label>
                        <toggle-switch
                            :model-value="siteSettings?.orderAcceptedFirst"
                            input-id="orderAcceptedFirst"
                            binary
                            @update:model-value="updateSiteSetting('orderAcceptedFirst', $event)"
                        />
                        <label for="reverseNivForecastSign">Show short NIV forecasts as positive</label>
                        <toggle-switch
                            :model-value="siteSettings?.reverseNivForecastSign"
                            input-id="reverseNivForecastSign"
                            binary
                            @update:model-value="updateSiteSetting('reverseNivForecastSign', $event)"
                        />
                        <label for="reverseNivSign">Show short NIV outturn as positive</label>
                        <toggle-switch
                            :model-value="siteSettings?.reverseNivSign"
                            input-id="reverseNivSign"
                            binary
                            @update:model-value="updateSiteSetting('reverseNivSign', $event)"
                        />
                        <label for="nivInMwh">Show NIV outturn in MWh</label>
                        <toggle-switch
                            :model-value="siteSettings?.nivInMwh"
                            input-id="nivInMwh"
                            binary
                            @update:model-value="updateSiteSetting('nivInMwh', $event)"
                        />
                        <label for="filterUnacceptedUnwinds">Hide unaccepted unwinds</label>
                        <toggle-switch
                            :model-value="siteSettings?.filterUnacceptedUnwinds"
                            input-id="filterUnacceptedUnwinds"
                            binary
                            @update:model-value="updateSiteSetting('filterUnacceptedUnwinds', $event)"
                        />
                        <label for="filterAcceptedUnwinds">Hide accepted unwinds</label>
                        <toggle-switch
                            :model-value="siteSettings?.filterAcceptedUnwinds"
                            input-id="filterAcceptedUnwinds"
                            binary
                            @update:model-value="updateSiteSetting('filterAcceptedUnwinds', $event)"
                        />
                        <label for="filterTrackedPlants">Filter table to plants being tracked</label>
                        <toggle-switch
                            :model-value="siteSettings?.filterTrackedPlants"
                            input-id="filterTrackedPlants"
                            binary
                            @update:model-value="updateSiteSetting('filterTrackedPlants', $event)"
                        />
                        <label for="useColouredFlagsForFlaggedBoaActions">Use coloured flags for flagged BOA actions</label>
                        <toggle-switch
                            :model-value="siteSettings?.useColouredFlagsForFlaggedBoaActions"
                            input-id="useColouredFlagsForFlaggedBoaActions"
                            binary
                            @update:model-value="updateSiteSetting('useColouredFlagsForFlaggedBoaActions', $event)"
                        />
                    </div>
                </div>
            </div>
            <div v-show="settingsTabInView === 'chartsAndTables'">
                <div class="sub-section">
                    <h4>Data Formatting:</h4>
                    <div class="sub-row">
                        <label for="timeFormat">Time format</label>
                        <select-button
                            :model-value="selectedTimeFormatSetting"
                            id="timeFormat"
                            :options="timeFormatOptions"
                            option-label="name"
                            option-value="id"
                            :allow-empty="false"
                            @update:model-value="updateSiteSetting('timeFormatValue', $event)"
                        />
                    </div>
                </div>
                <div class="sub-section">
                    <h4>Charts:</h4>
                    <div class="sub-row">
                        <label for="showChartSeriesOnHover">Show all series on chart hover</label>
                        <select-button
                            :model-value="showChartSeriesOnHover"
                            id="showChartSeriesOnHover"
                            :options="showAllSeriesOptions"
                            option-label="title"
                            option-value="value"
                            :allow-empty="false"
                            @update:model-value="updateSiteSetting('chartHoverChoice', $event)"
                        />
                        <label for="tooltipNearestPoint">
                            Show nearest point for series tooltip if no value for time
                            <info-icon-with-tooltip
                                direction="top"
                                tooltip-text="This will allow charts of different granularities to show the nearest value for all series.<br />Please note this will require a manual refresh when changed."
                            />
                        </label>
                        <select-button
                            :model-value="showTooltipNearestPoint"
                            id="tooltipNearestPoint"
                            :options="showHideOptions"
                            option-label="title"
                            option-value="value"
                            :allow-empty="false"
                            @update:model-value="updateSiteSetting('showNearestPointOnTooltip', $event)"
                        />
                        <label for="stepChart">Chart type</label>
                        <select-button
                            :model-value="chartsAreStep"
                            id="stepChart"
                            :options="chartTypeOptions"
                            option-label="title"
                            option-value="value"
                            :allow-empty="false"
                            @update:model-value="updateSiteSetting('stepChartOption', $event)"
                        />
                        <label for="fullChartingFunctionality">
                            Full charting functionality for large charts
                            <info-icon-with-tooltip
                                direction="top"
                                tooltip-text="By default Enact disables certain charting functionality for large (&gt;500 points) charts to speed up performance - including disabling live updates.<br />
                                Enabling this will enable full functionality on all charts, but may cause your browser to slow-down and/or freeze/crash."
                            />
                        </label>
                        <select-button
                            :model-value="allowFullCharting"
                            id="fullChartingFunctionality"
                            :options="onOffOptions"
                            option-label="title"
                            option-value="value"
                            :allow-empty="false"
                            @update:model-value="updateSiteSetting('fullChartFunctionality', $event)"
                        />
                        <label for="showFullDay">
                            Full day mode
                            <info-icon-with-tooltip
                                direction="top"
                                tooltip-text="By default, chart time axes span up until the latest time point with data.
                                Enabling this option will extend all the chart's time axes to the full day."
                            />
                        </label>
                        <select-button
                            :model-value="showFullDay"
                            id="showFullDay"
                            :options="onOffOptions"
                            option-label="title"
                            option-value="value"
                            :allow-empty="false"
                            @update:model-value="updateSiteSetting('fullDAChartAxes', $event)"
                        />
                    </div>
                </div>
                <div class="sub-section">
                    <h4>Tables:</h4>
                    <div class="sub-row">
                        <label for="tableUnits">Table units</label>
                        <select-button
                            :model-value="selectedTableUnitsSetting"
                            id="tableUnits"
                            :options="tableUnitsOptions"
                            option-label="name"
                            option-value="id"
                            :allow-empty="false"
                            @update:model-value="updateSiteSetting('tableUnitValue', $event)"
                        />
                        <label for="downloadExcelFilesAsCsv">
                            Excel download file type
                            <info-icon-with-tooltip
                                direction="top"
                                tooltip-text="Please note this will change the extension from .XLS to .CSV.<br />Numbers will be displayed as strings in Excel, however this will remove the Excel &ldquo;Unknown sources&#8221; warning."
                            />
                        </label>
                        <select-button
                            :model-value="downloadExcelFilesAsCsv"
                            id="downloadExcelFilesAsCsv"
                            :options="excelFileTypeOptions"
                            option-label="title"
                            option-value="value"
                            :allow-empty="false"
                            @update:model-value="updateSiteSetting('excelAsCsv', $event)"
                        />
                    </div>
                </div>
            </div>
            <div v-show="settingsTabInView === 'plants'" class="plant-group-selector-wrapper">
                <div class="settings-grid">
                    <label for="plantDisplayOption">Plant display option</label>
                    <select-button
                        :model-value="selectedPlantDisplayOption"
                        id="plantDisplayOption"
                        :options="plantDisplayOptions"
                        option-label="name"
                        option-value="id"
                        :allow-empty="false"
                        @update:model-value="updateSiteSetting('plantDisplayOption', $event)"
                    />
                </div>
                <div class="flex-column-with-gap">
                    <h4>My plant groups</h4>
                    <label class="plant-group-label" for="plantGroupSelect">Select plant group</label>
                    <crud-selector
                        v-model="selectedPlantGroupName"
                        input-id="plantGroupSelect"
                        name="group"
                        :headers-by-mode="crudHeadersByMode"
                        :options="plantGroupIds"
                        @update:selection="selectPlantGroup"
                        @create="createPlantGroup"
                        @update="updatePlantGroupName"
                        @delete="deletePlantGroup"
                    />
                    <div class="plant-selector-wrapper">
                        <div class="row">
                            <label for="addPlantToGroup">Add plant to group</label>
                            <plant-selector
                                input-id="addPlantToGroup"
                                :selected-plant-ids="currentSelectedPlant ? currentSelectedPlant.id : null"
                                @update:model-value-single="addPlantToGroup"
                            />
                            <div v-if="selectedPlantGroupName" class="colour-selector-wrapper">
                                <label for="plantGroupColour">Plant group colour</label>
                                <colour-selector input-id="plantGroupColour" :colour="selectedPlantGroupColourSettings.colour" @update:colour="updatePlantGroupColour" />
                            </div>
                        </div>
                        <ul v-if="selectedPlantGroupName" class="plant-list">
                            <li v-for="plant in selectedGroupPlants" :key="`plant-${plant.id}`">
                                <div class="plant-list-item-wrapper">
                                    <icofont-icon :background-colour="plant.colour" :icon="plant.icon" />
                                    <div>
                                        <div class="name">{{ plant.name }}</div>
                                        <div v-if="plant.id">{{ plant.id }}</div>
                                        <div v-if="plant.owner">{{ plant.owner }}</div>
                                    </div>
                                </div>
                                <primevue-button class="remove-plant-button" text @click="removePlantFromGroup(plant)">
                                    <font-awesome-icon class="icon" :icon="['fas', 'times']" />
                                </primevue-button>
                            </li>
                        </ul>
                    </div>
                    <div v-if="selectedPlantGroupName" class="flex-row-with-gap">
                        <label for="plantGroupColourOverride">
                            Highlight plants in tables with group colour
                        </label>
                        <checkbox
                            :model-value="selectedPlantGroupColourSettings.override"
                            input-id="plantGroupColourOverride"
                            binary
                            @update:model-value="updatePlantGroupColourOverride"
                        />
                    </div>
                </div>

            </div>
            <benchmark-pools-settings-panel v-show="settingsTabInView === 'pools'" :active="settingsTabInView === 'pools'" ref="poolsPanel" />
            <div v-show="settingsTabInView === 'heartbeat'" class="">
                <div class="title">
                    Heartbeat test (pushes every 5 seconds)
                    <info-icon-with-tooltip tooltip-text="This should update every 5 seconds.<br />If it isn't updating, then please contact Enact Helpdesk as soon as possible." direction="top" />
                </div>
                <div class="description">Last heartbeat received at: {{lastReceivedHeartbeat}}</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import PrimevueButton from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import InputNumber from 'primevue/inputnumber';
import SelectButton from 'primevue/selectbutton';
import { IFlagOption } from '@/interfaces/general/IFlagOption.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { TCrudMode } from '@/types/general/TCrudMode.ts';
import { unique } from '@/utils/Unique.ts';
import ColourSelector from '@/components/powerMarkets/common/ColourSelector.vue';
import CrudSelector from '@/components/powerMarkets/selectors/CrudSelector.vue';
import IcofontIcon from '@/components/powerMarkets/common/IcofontIcon.vue';
import PlantSelector from '@/components/powerMarkets/selectors/PlantSelector.vue';
import CountrySelector from '@/components/powerMarkets/selectors/CountrySelector.vue';
import TimePeriodSettings from '@/components/siteSettings/TimePeriodSettings.vue';
import BenchmarkPoolsSettingsPanel from '@/components/siteSettings/BenchmarkPoolsSettingsPanel.vue';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useUserSettingsActions } from '@/actions/powerMarkets/userSettingsActions.ts';
import { ISiteSettings } from '@/interfaces/powerMarkets/settings/ISiteSettings.ts';
import { IPlant } from '@/interfaces/powerMarkets/plant/IPlant.ts';
import { useDataHubStore } from '@/stores/powerMarkets/dataHubStore.ts';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import { LockedAccountEnum } from '@/enums/powerMarkets/LockedAccountEnum.ts';
import { TimeFormatEnum } from '@/enums/powerMarkets/TimeFormatEnum.ts';
import ToggleSwitch from 'primevue/toggleswitch';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { getPlantGroupColourSettings } from '@/helpers/powerMarkets/plantGroupSettings.ts';
import { PowerMarketsSettingsTab } from '@/types/siteSettings/SiteSettingsTypes.ts';
import { useIndexStore } from '@/stores/benchmarks/indexStore.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import type { IPlantGroupSettingsUpdate } from '@/interfaces/powerMarkets/settings/IPlantGroupSettings.ts';

const settingsTabInView = defineModel<PowerMarketsSettingsTab>('tab', { required: true });

const indexStore = useIndexStore();
const hasPoolAccess = computed(() => indexStore.userCustomAccessIndexCountries.some(country => country.accessKey === 'Gb'));
const poolsPanel = ref<InstanceType<typeof BenchmarkPoolsSettingsPanel> | null>(null);
async function requestLeave (): Promise<boolean> {
    return await poolsPanel.value?.requestLeave() ?? true;
}
async function changeSettingsTab (tab: string | number): Promise<void> {
    if (tab === settingsTabInView.value || !(await requestLeave())) return;
    settingsTabInView.value = tab as PowerMarketsSettingsTab;
}
defineExpose({ requestLeave });

const userSettingsActions = useUserSettingsActions();
const userSettingsStore = useUserSettingsStore();
const dataHubStore = useDataHubStore();
const dataStore = useDataStore();
const uiStore = useUIStore();
const { isMobileView } = storeToRefs(uiStore);

const currentSelectedPlant = ref<IPlant | null>(null);
const bidColour = ref<string>(userSettingsStore.userSettings.otherSettings.data.bidColour);
const offerColour = ref<string>(userSettingsStore.userSettings.otherSettings.data.offerColour);

const plantGroupsById = computed<Record<string, Array<string>>>(() => userSettingsStore.userSettings.otherSettings.data.chosenPlantsByGroup);
const plantGroupIds = computed<Array<string>>(() => Object.keys(plantGroupsById.value));
const siteSettings = computed<ISiteSettings>(() => userSettingsStore.userSettings.otherSettings.data);

const chosenPlantGroupName = userSettingsStore.userSettings.otherSettings.data.chosenPlantGroupName;
const selectedPlantGroupName = ref<string | null>(
    chosenPlantGroupName && plantGroupIds.value.includes(chosenPlantGroupName)
        ? chosenPlantGroupName
        : plantGroupIds.value[0] ?? null,
);

const selectedSideNavigationSetting = computed<string>(() => (siteSettings.value.sideNavVisible ? 'shown' : 'hidden'));
const selectedLockAccountSetting = computed<string>(() => siteSettings.value.lockedAccount);
const selectedTabUpdatesSetting = computed<string>(() => siteSettings.value.tabUpdateValue);
const selectedTimeFormatSetting = computed<string>(() => siteSettings.value.timeFormatValue);
const selectedTableUnitsSetting = computed<string>(() => siteSettings.value.tableUnitValue);
const showChartSeriesOnHover = computed<boolean>(() => siteSettings.value.chartHoverChoice);
const showTooltipNearestPoint = computed<boolean>(() => siteSettings.value.showNearestPointOnTooltip);
const chartsAreStep = computed<boolean>(() => siteSettings.value.stepChartOption);
const allowFullCharting = computed<boolean>(() => siteSettings.value.fullChartFunctionality);
const showFullDay = computed<boolean>(() => siteSettings.value.fullDAChartAxes);
const downloadExcelFilesAsCsv = computed<boolean>(() => siteSettings.value.excelAsCsv);
const selectedPlantGroupColourSettings = computed<{ override: boolean; colour: string }>(() => {
    if (selectedPlantGroupName.value) return getPlantGroupColourSettings(siteSettings.value, selectedPlantGroupName.value);
    return {
        override: false,
        colour: siteSettings.value.defaultPlantGroupColour,
    };
});
const selectedElexonDelayAlert = computed<number>(() => siteSettings.value.minutesDelayToReport);
const selectedPlantDisplayOption = computed<string>(() => siteSettings.value.plantDisplayOption ?? 'id');

const lastReceivedHeartbeat = computed<string>(() => dataHubStore.lastHeartbeat);

const selectedGroupPlants = computed<Array<IPlant>>(() => {
    if (!selectedPlantGroupName.value) return [];
    return unique(dataStore.allPlants.filter(plant => (selectedPlantGroupName.value ? plantGroupsById.value[selectedPlantGroupName.value]?.includes(plant.id) ?? false : false)));
});

const crudHeadersByMode = computed<Record<TCrudMode, string>>(() => ({
    create: 'Create new plant group',
    update: `Update group name${selectedPlantGroupName.value ? ` for ${selectedPlantGroupName.value}` : ''}`,
    delete: `Delete group${selectedPlantGroupName.value ? ` ${selectedPlantGroupName.value}` : ''}?`,
}));

const lockAccountOptions: Array<IIdName> = [
    { name: 'Unlocked', id: LockedAccountEnum.Unlocked },
    { name: 'Lock saving', id: LockedAccountEnum.UploadLocked, info: 'Editing enact will be allowed, but changes won\'t be saved in your account.' },
];

const tabUpdatesOptions: Array<IIdName> = [
    { name: 'None', id: 'none', info: 'Updating one tab/window will not effect any others opened.' },
    { name: 'Warning', id: 'warn', info: 'Updating one tab/window of Enact will alert any others opened, but not change them.<br />Please note this might be intrusive.' },
    { name: 'Update', id: 'update', info: 'Updating one tab/window of Enact will attempt to update any others opened.<br />Please note this is an experimental feature.' },
];

const sideNavigationOptions: Array<IIdName> = [
    { name: 'Shown', id: 'shown' },
    { name: 'Hidden', id: 'hidden' },
];

const timeFormatOptions: Array<IIdName> = [
    { name: '12 hour', id: TimeFormatEnum.TwelveHour },
    { name: '24 hour', id: TimeFormatEnum.TwentyFourHour },
];

const tableUnitsOptions: Array<IIdName> = [
    { name: 'Show on hover', id: 'hover' },
    { name: 'In cell', id: 'cell' },
    { name: 'In header', id: 'header' },
    { name: 'Hidden', id: 'hidden' },
];

const plantDisplayOptions: Array<IIdName> = [
    { name: 'ID (Default)', id: 'id' },
    { name: 'Name', id: 'name' },
];

const showAllSeriesOptions: Array<IFlagOption> = [
    { title: 'All series', value: true },
    { title: 'Single series', value: false },
];

const showHideOptions: Array<IFlagOption> = [
    { title: 'Show', value: true },
    { title: 'Hide', value: false },
];

const chartTypeOptions: Array<IFlagOption> = [
    { title: 'Step', value: true },
    { title: 'Line', value: false },
];

const onOffOptions: Array<IFlagOption> = [
    { title: 'On', value: true },
    { title: 'Off', value: false },
];

const excelFileTypeOptions: Array<IFlagOption> = [
    { title: 'CSV', value: true },
    { title: 'XLS', value: false },
];

function updateBidColour (newBidColour: string): void {
    userSettingsStore.setSiteSettings('bidColour', newBidColour, true);
}

function updateOfferColour (newOfferColour: string): void {
    userSettingsStore.setSiteSettings('offerColour', newOfferColour, true);
}

function addPlantToGroup (newPlant: IPlant): void {
    const newPlantId = newPlant.id;
    if (!selectedPlantGroupName.value || !siteSettings.value) return;

    if (plantGroupsById.value[selectedPlantGroupName.value].includes(newPlantId)) return;

    const newChosenPlantsByGroup = {
        ...plantGroupsById.value,
        [selectedPlantGroupName.value]: [
            ...plantGroupsById.value[selectedPlantGroupName.value],
            newPlant.id,
        ],
    };

    userSettingsActions.savePlantGroupSettings({ chosenPlantsByGroup: newChosenPlantsByGroup });
}

function removePlantFromGroup (plant: IPlant): void {
    if (!selectedPlantGroupName.value || !siteSettings.value) return;

    const newChosenPlantsByGroup = {
        ...plantGroupsById.value,
        [selectedPlantGroupName.value]: plantGroupsById.value[selectedPlantGroupName.value].filter(id => id !== plant.id),
    };

    userSettingsActions.savePlantGroupSettings({ chosenPlantsByGroup: newChosenPlantsByGroup });
}

function createPlantGroup (newGroupName: string): void {
    const newChosenPlantsByGroup = {
        ...plantGroupsById.value,
        [newGroupName]: [],
    };

    selectedPlantGroupName.value = newGroupName;
    userSettingsActions.savePlantGroupSettings({
        chosenPlantsByGroup: newChosenPlantsByGroup,
        chosenPlantGroupName: newGroupName,
    });
}

function updatePlantGroupName (newGroupName: string): void {
    if (!selectedPlantGroupName.value) return;

    const plantGroupsCloned = { ...plantGroupsById.value };
    plantGroupsCloned[newGroupName] = plantGroupsCloned[selectedPlantGroupName.value];
    delete plantGroupsCloned[selectedPlantGroupName.value];

    const updatedPlantGroupColourOverrideByGroup = { ...siteSettings.value.plantGroupColourOverrideByGroup };
    const update: IPlantGroupSettingsUpdate = {
        chosenPlantGroupName: newGroupName,
        chosenPlantsByGroup: plantGroupsCloned,
    };

    if (updatedPlantGroupColourOverrideByGroup[selectedPlantGroupName.value]) {
        updatedPlantGroupColourOverrideByGroup[newGroupName] = updatedPlantGroupColourOverrideByGroup[selectedPlantGroupName.value];
        delete updatedPlantGroupColourOverrideByGroup[selectedPlantGroupName.value];
        update.plantGroupColourOverrideByGroup = updatedPlantGroupColourOverrideByGroup;
    }

    selectedPlantGroupName.value = newGroupName;
    userSettingsActions.savePlantGroupSettings(update);
}

function deletePlantGroup (): void {
    if (!selectedPlantGroupName.value) return;

    const updatedChosenPlantsByGroup = Object.fromEntries(
        Object.entries(plantGroupsById.value).filter(([key]) => key !== selectedPlantGroupName.value),
    );

    const updatedPlantGroupColourOverrideByGroup = Object.fromEntries(
        Object.entries(siteSettings.value.plantGroupColourOverrideByGroup).filter(([key]) => key !== selectedPlantGroupName.value),
    );

    const nextGroupName = Object.keys(updatedChosenPlantsByGroup)[0] ?? null;
    userSettingsActions.savePlantGroupSettings({
        chosenPlantsByGroup: updatedChosenPlantsByGroup,
        plantGroupColourOverrideByGroup: updatedPlantGroupColourOverrideByGroup,
        chosenPlantGroupName: nextGroupName,
    });
    selectedPlantGroupName.value = nextGroupName;
}

function updatePlantGroupColour (colour: string): void {
    if (!siteSettings.value || !selectedPlantGroupName.value) return;

    const newPlantGroupColourOverrideByGroup = {
        ...siteSettings.value.plantGroupColourOverrideByGroup,
        [selectedPlantGroupName.value]: {
            ...getPlantGroupColourSettings(siteSettings.value, selectedPlantGroupName.value),
            colour,
        },
    };

    userSettingsActions.savePlantGroupSettings({ plantGroupColourOverrideByGroup: newPlantGroupColourOverrideByGroup });
}

function updatePlantGroupColourOverride (override: boolean): void {
    if (!selectedPlantGroupName.value) return;

    const newPlantGroupColourOverrideByGroup = {
        ...siteSettings.value.plantGroupColourOverrideByGroup,
        [selectedPlantGroupName.value]: {
            ...getPlantGroupColourSettings(siteSettings.value, selectedPlantGroupName.value),
            override,
        },
    };

    userSettingsActions.savePlantGroupSettings({ plantGroupColourOverrideByGroup: newPlantGroupColourOverrideByGroup });
}

function selectPlantGroup (groupName: string | null): void {
    selectedPlantGroupName.value = groupName;
    userSettingsActions.savePlantGroupSettings({ chosenPlantGroupName: groupName });
}

function updateSiteSetting<K extends keyof ISiteSettings> (prop: K, newSetting: ISiteSettings[K]): void {
    userSettingsActions.updateUserSettings(prop, newSetting, true);
}
</script>

<style lang="scss" scoped>
.settings-wrapper {
    display: grid;
    grid-template-rows: auto 1fr;
    height: 100%;
    overflow: hidden;

    .settings-content {
        overflow: auto;
        padding: var(--defaultSpacing);
    }
}

.settings-tab-nav {
    padding: 0 var(--smallSpacing);
}

.checkbox-padding{
    margin-right: var(--largeSpacing);
}

.title {
    font-size: var(--defaultFontSize);
    font-weight: bold;
}

.description {
    margin-top: var(--extraLargeSpacing);
}

h4 {
    margin-bottom: var(--largeSpacing);
}

label {
    font-size: var(--defaultFontSize);
    user-select: none;
}

.colour-selector-wrapper {
    align-items: center;
    display: flex;
    gap: var(--defaultSpacing);
    width: 100%;
}

.settings-grid {
    align-items: center;
    display: grid;
    gap: var(--defaultSpacing) var(--largeSpacing);
    grid-template-columns: auto 1fr;

    @include responsive(sm) {
        grid-template-columns: 1fr;
    }
}

.toggle-switch-grid {
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);
    > * {
        display: flex;
        gap: var(--defaultSpacing);
        align-items: center;
    }
}

.row {
    display: flex;
    gap: var(--defaultSpacing);
    align-items: center;
    font-size: var(--smallFontSize);
}

.sub-section {
    padding-top: var(--largeSpacing);
    grid-column: span 2;

    .sub-row {
        padding-left: var(--defaultSpacing);
        align-items: center;
        display: grid;
        gap: var(--defaultSpacing);
        grid-template-columns: auto 1fr;
        margin-bottom: var(--defaultSpacing);
    }

    &.boa-sub-section {
        .sub-row {
            grid-template-columns: auto 1fr;
            gap: var(--defaultSpacing);
        }
    }
}

.plant-crud-overlay {
    padding: var(--defaultSpacing);
    background-color: var(--neutral300);
    max-width: 200px;
    text-align: center;
    line-height: 1.5;
    border-radius: var(--borderRadiusOuter);
    box-shadow: var(--shadow3);

    .button-row {
        display: flex;
        gap: var(--defaultSpacing);
        padding-top: var(--defaultSpacing);
        justify-content: space-around;

    }
}

.confirm-message {
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);

    .validation-message {
        color: var(--lcpWatermelon);
    }

    label {
        display: none;
    }

    input {
        width: 100%;
    }
}

.plant-group-selector-wrapper {
    width: 100%;
    max-width: 60rem;
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
}

.plant-selector-wrapper {
    margin: var(--defaultSpacing) 0;
    width: 100%;

    .plant-list {
        font-size: var(--smallFontSize);
        max-height: 50rem;
        line-height: 1.5;
        list-style: none;
        overflow: auto;
        padding: var(--defaultSpacing) var(--defaultSpacing) var(--defaultSpacing) 0;

        @include responsive(sm) {
            .row {
                flex-wrap: wrap;
            }
        }

        li {
            background-color: var(--neutral0);
            margin-bottom: var(--defaultSpacing);
            border-radius: var(--borderRadiusOuter);
            padding: var(--defaultSpacing);

            display: flex;
            justify-content: space-between;

            .plant-list-item-wrapper {
                display: flex;
                gap: var(--defaultSpacing);

                .name {
                    font-weight: var(--boldFontWeight);
                    font-size: var(--defaultFontSize)
                }
            }

            .icon {
                color: var(--lcpBlue);
                font-size: var(--largeFontSize);
            }
        }
    }
}

</style>
